import {
  getTasks as getTasksService,
  getTasksWithParams as getTasksWithParamsService,
  getTopPriorityTasks as getTopPriorityTasksService,
  getTeamTasks as getTeamTasksService,
  createTask as createTaskService,
  updateTask as updateTaskService,
  updateTaskStatus as updateTaskStatusService,
  createTaskReview as createTaskReviewService,
  deleteTask as deleteTaskService,
  getEmployeeTaskDateRanges as getEmployeeTaskDateRangesService,
} from "services/company/projectManagement/task/taskService";
import {
  getTasksStart,
  getTasksSuccess,
  getTasksFailure,
  createTaskStart,
  createTaskSuccess,
  createTaskFailure,
  updateTaskStart,
  updateTaskSuccess,
  updateTaskFailure,
  updateTaskStatusStart,
  updateTaskStatusOptimistic,
  rollbackTaskStatus,
  updateTaskStatusSuccess,
  updateTaskStatusFailure,
  deleteTaskStart,
  deleteTaskSuccess,
  deleteTaskFailure,
  getEmployeeTaskDateRangesStart,
  getEmployeeTaskDateRangesSuccess,
  getEmployeeTaskDateRangesFailure,
  getTopPriorityTasksStart,
  getTopPriorityTasksSuccess,
  getTopPriorityTasksFailure,
} from "./taskSlice";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

// Fetch all tasks
export const fetchTasks =
  (params = null) =>
    async (dispatch) => {
      try {
        dispatch(getTasksStart());
        let response;
        if (params?.teams) {
          const { teams, ...restParams } = params;
          response = await getTeamTasksService(
            Object.keys(restParams).length ? restParams : null
          );
        } else {
          response = params
            ? await getTasksWithParamsService(params)
            : await getTasksService();
        }

        if (response?.meta?.status === true && response?.meta?.code === 200) {
          dispatch(getTasksSuccess(response.data));
          return { success: true, data: response.data };
        } else {
          const errorMsg = response?.meta?.message || "Failed to fetch tasks";
          dispatch(getTasksFailure(errorMsg));
          showErrorToast(errorMsg);
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Fetch tasks error:", error);
        const errorMsg =
          error?.response?.data?.meta?.message ||
          error.message ||
          "Failed to fetch tasks";
        dispatch(getTasksFailure(errorMsg));
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    };

// Fetch team tasks (tasks assigned to reportees in org tree)
export const fetchTeamTasks =
  (params = null) =>
    async (dispatch) => {
      try {
        dispatch(getTasksStart());
        const response = await getTeamTasksService(params);

        if (response?.meta?.status === true && response?.meta?.code === 200) {
          dispatch(getTasksSuccess(response.data));
          return { success: true, data: response.data };
        } else {
          const errorMsg = response?.meta?.message || "Failed to fetch team tasks";
          dispatch(getTasksFailure(errorMsg));
          showErrorToast(errorMsg);
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Fetch team tasks error:", error);
        const errorMsg =
          error?.response?.data?.meta?.message ||
          error.message ||
          "Failed to fetch team tasks";
        dispatch(getTasksFailure(errorMsg));
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    };


// Create a new task
export const createNewTask = (taskData) => async (dispatch) => {
  try {
    dispatch(createTaskStart());

    // Check if taskData is FormData (for file uploads)
    const isFormData = taskData instanceof FormData;

    // If it's FormData, send it directly with multipart headers
    // If it's JSON, send as is
    let response;
    if (isFormData) {
      response = await createTaskService(taskData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await createTaskService(taskData);
    }

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(createTaskSuccess(response.data));
      showSuccessToast(response?.meta?.message || "Task created successfully!");
      return { success: true, data: response.data };
    } else {
      const errorMsg = response?.meta?.message || "Failed to create task";
      dispatch(createTaskFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Create task error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to create task";
    dispatch(createTaskFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Update a task
export const updateExistingTask = (taskId, taskData) => async (dispatch) => {
  try {
    dispatch(updateTaskStart());
    const response = await updateTaskService(taskId, taskData);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(updateTaskSuccess(response.data));
      showSuccessToast(response?.meta?.message || "Task updated successfully!");
      return { success: true, data: response.data };
    } else {
      const errorMsg = response?.meta?.message || "Failed to update task";
      dispatch(updateTaskFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Update task error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to update task";
    dispatch(updateTaskFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

// Update task status
export const updateExistingTaskStatus =
  (taskId, statusData) => async (dispatch, getState) => {
    const currentTasks = getState().companyTask?.tasks || [];
    const currentTask = currentTasks.find(
      (t) => String(t.id) === String(taskId),
    );
    const previousStatus = currentTask ? currentTask.status : null;

    try {
      const targetStatus =
        statusData instanceof FormData
          ? statusData.get("status")
          : statusData?.status;

      if (targetStatus) {
        dispatch(
          updateTaskStatusOptimistic({
            taskId,
            status: targetStatus,
          }),
        );
      }

      dispatch(updateTaskStatusStart());
      const response = await updateTaskStatusService(taskId, statusData);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(updateTaskStatusSuccess(response.data));
        showSuccessToast(
          response?.meta?.message || "Task status updated successfully!",
        );
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to update task status";
        if (previousStatus) {
          dispatch(rollbackTaskStatus({ taskId, previousStatus }));
        }
        dispatch(updateTaskStatusFailure(errorMsg));
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Update task status error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to update task status";
      if (previousStatus) {
        dispatch(rollbackTaskStatus({ taskId, previousStatus }));
      }
      dispatch(updateTaskStatusFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

export const createTaskReview = (taskId, reviewData) => async (dispatch) => {
  try {
    const response = await createTaskReviewService(taskId, reviewData);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      showSuccessToast(response?.meta?.message || "Review added successfully!");

      return {
        success: true,
        data: response.data,
      };
    }

    const errorMsg = response?.meta?.message || "Failed to add review";

    showErrorToast(errorMsg);

    return {
      success: false,
      error: errorMsg,
    };
  } catch (error) {
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to add review";

    showErrorToast(errorMsg);

    return {
      success: false,
      error: errorMsg,
    };
  }
};

// Delete a task
export const deleteExistingTask = (taskId) => async (dispatch) => {
  try {
    dispatch(deleteTaskStart());
    const response = await deleteTaskService(taskId);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(deleteTaskSuccess(taskId));
      showSuccessToast(response?.meta?.message || "Task deleted successfully!");
      return { success: true };
    } else {
      const errorMsg = response?.meta?.message || "Failed to delete task";
      dispatch(deleteTaskFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Delete task error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to delete task";
    dispatch(deleteTaskFailure(errorMsg));
    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

export const fetchEmployeeTaskDateRanges = (employeeId) => async (dispatch) => {
  try {
    dispatch(getEmployeeTaskDateRangesStart());

    const response = await getEmployeeTaskDateRangesService(employeeId);

    if (response?.meta?.status === true) {
      dispatch(getEmployeeTaskDateRangesSuccess(response.data));

      return response.data;
    }

    dispatch(getEmployeeTaskDateRangesFailure());

    return [];
  } catch (err) {
    dispatch(getEmployeeTaskDateRangesFailure());

    return [];
  }
};

// Fetch top 10 priority tasks
export const fetchTopPriorityTasks = (params = null) => async (dispatch) => {
  try {
    dispatch(getTopPriorityTasksStart());
    const response = await getTopPriorityTasksService(params);

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      dispatch(getTopPriorityTasksSuccess(response.data));
      return { success: true, data: response.data };
    } else {
      const errorMsg = response?.meta?.message || "Failed to fetch top priority tasks";
      dispatch(getTopPriorityTasksFailure(errorMsg));
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Fetch top priority tasks error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to fetch top priority tasks";
    dispatch(getTopPriorityTasksFailure(errorMsg));
    return { success: false, error: errorMsg };
  }
};
